import React, { useMemo } from "react";
import styled from "styled-components";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const RichTextEditorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.span`
  color: rgb(33, 37, 67);
  font-size: 14px;
  font-family: Montserrat, sans-serif;
  font-weight: 500;
  line-height: 160%;
  text-align: left;
`;

const EditorWrapper = styled.div`
  border: solid 1px
    ${(props) => (props.error ? "rgb(220, 38, 38)" : "rgb(224, 228, 244)")};
  border-radius: 8px;
  background: rgb(255, 255, 255);
  transition: border-color 0.2s;

  &:focus-within {
    border-color: rgb(97, 98, 197);
  }

  .quill {
    font-family: Montserrat, sans-serif;
  }

  .ql-toolbar {
    border: none;
    border-bottom: solid 1px rgb(224, 228, 244);
    border-radius: 8px 8px 0 0;
    background: rgb(250, 250, 252);
    padding: 8px;
  }

  .ql-container {
    border: none;
    font-size: 16px;
    font-family: Montserrat, sans-serif;
    min-height: ${(props) => props.minHeight || "150px"};
  }

  .ql-editor {
    padding: 16px;
    color: rgb(33, 37, 67);
    font-weight: 500;
    line-height: 160%;
    min-height: ${(props) => props.minHeight || "150px"};
  }

  .ql-editor.ql-blank::before {
    color: rgb(179, 182, 195);
    font-style: normal;
    font-weight: 500;
  }

  .ql-snow .ql-stroke {
    stroke: rgb(33, 37, 67);
  }

  .ql-snow .ql-fill {
    fill: rgb(33, 37, 67);
  }

  .ql-snow .ql-picker-label {
    color: rgb(33, 37, 67);
  }

  .ql-toolbar button:hover,
  .ql-toolbar button:focus,
  .ql-toolbar button.ql-active {
    color: rgb(97, 98, 197);
  }

  .ql-toolbar button:hover .ql-stroke,
  .ql-toolbar button:focus .ql-stroke,
  .ql-toolbar button.ql-active .ql-stroke {
    stroke: rgb(97, 98, 197);
  }

  .ql-toolbar button:hover .ql-fill,
  .ql-toolbar button:focus .ql-fill,
  .ql-toolbar button.ql-active .ql-fill {
    fill: rgb(97, 98, 197);
  }
`;

const ErrorMessage = styled.span`
  color: rgb(220, 38, 38);
  font-size: 12px;
  font-family: Montserrat, sans-serif;
  margin-top: 4px;
`;

const RichTextEditor = ({
  label,
  placeholder = "Enter text...",
  value,
  onChange,
  error,
  errorMessage,
  minHeight = "150px",
  modules: customModules,
  formats: customFormats,
  ...props
}) => {
  const defaultModules = useMemo(
    () => ({
      toolbar: [
        [{ header: [1, 2, 3, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ align: [] }],
        ["link"],
        ["clean"],
      ],
    }),
    []
  );

  const defaultFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "align",
    "link",
  ];

  return (
    <RichTextEditorContainer>
      {label && <Label>{label}</Label>}
      <EditorWrapper error={error} minHeight={minHeight}>
        <ReactQuill
          theme="snow"
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          modules={customModules || defaultModules}
          formats={customFormats || defaultFormats}
          {...props}
        />
      </EditorWrapper>
      {error && errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
    </RichTextEditorContainer>
  );
};

export default RichTextEditor;